import { useTranslation } from 'react-i18next';
import { CommentHeader } from './CommentHeader';
import { MentionInput } from 'app/components/MentionInput';
import { useState, useRef } from 'react';
import { useComments } from 'entities/comment';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';

import { useEditorUiSetters } from 'app/state/contexts/EditorUiContext';
import { CommentItem } from './types';

const formatMessage = (text: string) => {
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, index) =>
    part.startsWith('@') ? (
      <span key={index} className="comment--mention">
        {part}
      </span>
    ) : (
      part
    )
  );
};

interface ResponseProps {
  response: CommentItem;
  activeResponse?: CommentItem | null;
  isActiveResponse?: CommentItem | null;
  setActiveResponse: (value: CommentItem | null) => void;
  sectionSuperId: string;
  setNewResponseComment: () => void;
  readOnly?: boolean;
}

export const Response = ({ response, activeResponse, setActiveResponse, sectionSuperId, setNewResponseComment, readOnly = false }: ResponseProps) => {
  const { t } = useTranslation(['comments', 'common']);

  const [responseComment, setResponseComment] = useState<CommentItem>(response);
  const responseRef = useRef<HTMLTextAreaElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const { updateCommentStatus, updateCommentMessageAsync, createCommentAsync, publishComment, deleteComment, refetchComments } = useComments(sectionSuperId);
  const { setDraftComment } = useSidebar();
  const { setIsDirty } = useEditorUiSetters();
  const saveResponse = async (_message?: string) => {
    setIsDirty(true);
    if (responseComment?.publishedAt) {
      await updateCommentMessageAsync({ commentId: responseComment.id, message: responseComment.message ?? '' });
    } else {
      const newResponseComment = { ...responseComment, message: responseComment.message, publishedAt: null };
      await createCommentAsync({ comment: newResponseComment });
    }
    setIsDirty(false);
  };
  const [save, cancel] = useDelayedSave((message: string) => saveResponse(message), 1500);

  const handleChange = (e: string) => {
    cancel();
    setResponseComment({ ...responseComment, message: e });
    save(e);
  };

  const handleResponseSubmit = async (e: React.MouseEvent) => {
    cancel();
    e.preventDefault();
    await saveResponse(responseComment.message);
    publishComment({ commentId: responseComment.id, publish: true });
    setIsUpdating(false);
    setDraftComment(null);
    setActiveResponse(null);
    refetchComments();
    setNewResponseComment();
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setDraftComment(null);
    setIsUpdating(false);

    setActiveResponse(null);
  };

  const actionHandler = (action: string, event: React.MouseEvent) => {
    event.stopPropagation();
    switch (action) {
      case 'edit':
        setIsUpdating(true);
        break;
      case 'delete':
        deleteComment(responseComment.id);
        break;
      case 'open':
      case 'close':
        updateCommentStatus({ commentId: responseComment.id, status: action !== 'open' });
        break;
      default:
        return;
    }
    setIsMenuOpen(false);
  };

  return (
    <div className={`${activeResponse ? 'active' : ''} ${!response?.publishedAt ? 'draft' : ''} ${response?.closedAt ? 'archived' : ''} comment`}>
      <CommentHeader
        isActiveComment={!!activeResponse}
        comment={response}
        isUpdating={isUpdating}
        setMenuOpen={setIsMenuOpen}
        menuOpen={isMenuOpen}
        actionHandler={actionHandler}
        readOnly={readOnly}
      />
      {!readOnly && (isUpdating || !responseComment?.updatedAt) ? (
        <>
          <MentionInput value={responseComment?.message ?? ''} onChange={handleChange} ref={responseRef} placeHolder={t('comments:response.placeholder')} />
          <div className="comment--buttons">
            <button onClick={handleCancel} className="btn btn-ghost btn-md">
              {t('common:button.cancel')}
            </button>
            <button
              onClick={async () => {
                cancel();
                await saveResponse(responseComment.message);
                publishComment({ commentId: responseComment.id, publish: false });
                setDraftComment(null);
                setIsUpdating(false);
                refetchComments();
                setNewResponseComment();
              }}
              className="btn btn-secondary btn-md"
            >
              {t('common:save-draft')}
            </button>
            <button onClick={handleResponseSubmit} className="btn btn-primary btn-md">
              {t('common:publish')}
            </button>
          </div>
        </>
      ) : (
        <div className="responsecomment--message">{formatMessage(responseComment?.message ?? '')}</div>
      )}
    </div>
  );
};
